import type { ImportPreview, ImportRecord, Vehicle, VehicleEvent } from '@msc-stock/shared';

/** API がエラーを返したときに、画面にそのまま出せる日本語を持って投げる。 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (!res.ok) {
    let message = `通信に失敗しました (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      /* JSON でないエラー応答はそのまま既定のメッセージを使う */
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function json(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  };
}

export const api = {
  me: () => request<{ authed: boolean; env: string }>('/api/auth/me'),
  login: (password: string) => request<{ authed: boolean }>('/api/auth/login', json({ password })),
  logout: () => request<{ authed: boolean }>('/api/auth/logout', { method: 'POST' }),

  vehicles: (params: URLSearchParams) =>
    request<{
      vehicles: Vehicle[];
      page: number;
      pageSize: number;
      total: number;
      purchaseTotal: number;
      saleTotal: number;
      pricedCount: number;
    }>(`/api/vehicles?${params.toString()}`),

  vehicle: (id: number) => request<{ vehicle: Vehicle; events: VehicleEvent[] }>(`/api/vehicles/${id}`),

  saveSale: (id: number, body: { salePrice: number | null; saleDate: string | null; buyer: string | null; saleNote: string | null }) =>
    request<{ vehicle: Vehicle }>(`/api/vehicles/${id}/sale`, { ...json(body), method: 'PATCH' }),

  editVehicle: (id: number, body: { maker?: string; model?: string; grade?: string; color?: string }) =>
    request<{ vehicle: Vehicle }>(`/api/vehicles/${id}`, { ...json(body), method: 'PATCH' }),

  setStatus: (id: number, body: { status: 'in_stock' | 'sold'; soldDate?: string | null }) =>
    request<{ vehicle: Vehicle }>(`/api/vehicles/${id}/status`, { ...json(body), method: 'PATCH' }),

  summary: () => request<Summary>('/api/stats/summary'),
  facets: () => request<Facets>('/api/stats/facets'),

  previewImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ preview: ImportPreview; filename: string }>('/api/imports/preview', {
      method: 'POST',
      body: form,
    });
  },
  commitImport: (body: { stagingKey: string; filename: string; snapshotDate: string | null; note: string | null }) =>
    request<{ result: { importId: number; added: number; sold: number; returned: number; updated: number; unchanged: number }; import: ImportRecord | null }>(
      '/api/imports/commit',
      json(body),
    ),
  imports: () => request<{ imports: ImportRecord[] }>('/api/imports'),
  importDetail: (id: number) =>
    request<{ import: ImportRecord; added: Vehicle[]; sold: Vehicle[] }>(`/api/imports/${id}`),
};

export interface GroupStat {
  key: string;
  count: number;
  purchaseTotal: number;
  saleTotal: number;
  priced: number;
}

export interface Summary {
  byDivision: Array<{
    division: string;
    status: string;
    count: number;
    purchaseTotal: number;
    saleTotal: number;
    priced: number;
    pricedCost: number;
  }>;
  makers: GroupStat[];
  suppliers: GroupStat[];
  models: GroupStat[];
  colors: GroupStat[];
  purchasedByMonth: Array<{ month: string; division: string; count: number; purchaseTotal: number }>;
  soldByMonth: Array<{ month: string; division: string; count: number; purchaseTotal: number; saleTotal: number }>;
  oldestInStock: Array<Record<string, unknown>>;
  saleMissingCount: number;
  lastImport: { id: number; snapshot_date: string | null; uploaded_at: string; filename: string } | null;
}

export interface Facets {
  makers: Array<{ value: string; count: number }>;
  models: Array<{ value: string; count: number }>;
  suppliers: Array<{ value: string; count: number }>;
  colors: Array<{ value: string; count: number }>;
}

/** 絞り込み結果全体のグループ集計 (メーカー別・仕入先別など)。 */
export function fetchGroups(params: URLSearchParams, by: string) {
  const p = new URLSearchParams(params);
  p.set('by', by);
  p.delete('page');
  p.delete('pageSize');
  return requestGroups(`/api/vehicles/group?${p.toString()}`);
}

async function requestGroups(path: string) {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError('グループ集計を取得できませんでした', res.status);
  return (await res.json()) as { by: string; groups: GroupStat[] };
}
